import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Icon,
  useBreakpointValue,
  Flex,
} from '@chakra-ui/react';
import { Sparkles, Train } from 'lucide-react';

const TourWelcomeModal = ({ open, onStart, onSkip, onRemindLater }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Modal
      isOpen={open}
      onClose={onSkip}
      isCentered
      size={isMobile ? 'sm' : 'md'}
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(6px)" />

      <ModalContent
        bg="white"
        borderRadius="32px"
        px={{ base: 4, md: 6 }}
        py={{ base: 6, md: 8 }}
        mx={4}
        position="relative"
        overflow="hidden"
        boxShadow="0 20px 60px rgba(0,0,0,0.15)"
      >
        {/* Bubble Background */}
        <Box
          position="absolute"
          top="-40px"
          right="-30px"
          w="140px"
          h="140px"
          bg="blue.100"
          borderRadius="full"
          opacity={0.7}
        />

        <Box
          position="absolute"
          bottom="-50px"
          left="-40px"
          w="160px"
          h="160px"
          bg="purple.100"
          borderRadius="full"
          opacity={0.6}
        />

        <ModalBody p={0}>
          <VStack spacing={6} align="center" textAlign="center">
            {/* Icon Bubble */}
            <Box
              bg="linear-gradient(135deg, #4F46E5, #7C3AED)"
              p={5}
              borderRadius="full"
              color="white"
              boxShadow="lg"
            >
              <Icon as={Sparkles} boxSize={8} />
            </Box>

            {/* Heading */}
            <VStack spacing={2}>
              <Text
                fontSize={{ base: '2xl', md: '3xl' }}
                fontWeight="bold"
                color="gray.800"
              >
                Welcome to SAM 🚀
              </Text>

              {/* LIVE TRAIN ANIMATION */}
              <Box w="100%" py={4}>
                <Flex
                  align="center"
                  justify="space-between"
                  position="relative"
                >
                  {/* Line */}
                  <Box
                    position="absolute"
                    top="50%"
                    left="0"
                    right="0"
                    h="4px"
                    bg="gray.200"
                    zIndex={0}
                    borderRadius="full"
                  />

                  {/* Animated Progress */}
                  <Box
                    position="absolute"
                    top="50%"
                    left="0"
                    h="4px"
                    bgGradient="linear(to-r, blue.400, purple.500)"
                    borderRadius="full"
                    zIndex={1}
                    animation="trainMove 3s linear infinite"
                  />

                  {/* Start */}
                  <VStack zIndex={2} spacing={1}>
                    <Box bg="green.500" color="white" p={2} borderRadius="full">
                      S
                    </Box>
                    <Text fontSize="xs" fontWeight="600">
                      Start
                    </Text>
                  </VStack>

                  {/* Train */}
                  <Box
                    position="absolute"
                    left="0"
                    zIndex={3}
                    animation="trainTravel 3s linear infinite"
                  >
                    <Icon as={Train} boxSize={7} color="blue.500" />
                  </Box>

                  {/* End */}
                  <VStack zIndex={2} spacing={1}>
                    <Box bg="red.500" color="white" p={2} borderRadius="full">
                      E
                    </Box>
                    <Text fontSize="xs" fontWeight="600">
                      End
                    </Text>
                  </VStack>
                </Flex>
              </Box>
              <Text
                fontSize={{ base: 'sm', md: 'md' }}
                color="gray.600"
                maxW="340px"
                lineHeight="tall"
              >
                Take a quick interactive tour and explore the platform features.
              </Text>
            </VStack>

            {/* Buttons */}
            <HStack
              spacing={4}
              pt={2}
              w="full"
              flexDirection={{ base: 'column', sm: 'row' }}
            >
              <Button
                w="full"
                size="lg"
                colorScheme="blue"
                borderRadius="full"
                onClick={onStart}
                bgGradient="linear(to-r, blue.500, purple.500)"
                _hover={{
                  bgGradient: 'linear(to-r, blue.600, purple.600)',
                  transform: 'translateY(-2px)',
                }}
                transition="all 0.2s"
              >
                Start Tour
              </Button>

              <Button
                w="full"
                size="lg"
                variant="ghost"
                borderRadius="full"
                color="gray.600"
                onClick={onSkip}
              >
                Skip
              </Button>

              <Button
                w="full"
                size="lg"
                variant="ghost"
                borderRadius="full"
                color="gray.600"
                onClick={onRemindLater}
              >
                Remind Me Later
              </Button>
            </HStack>
          </VStack>
        </ModalBody>

        {/* Animation CSS */}
        <style>
          {`
            @keyframes trainTravel {
              0% {
                left: 0%;
              }
              100% {
                left: calc(100% - 30px);
              }
            }

            @keyframes trainMove {
              0% {
                width: 0%;
              }
              100% {
                width: 100%;
              }
            }
          `}
        </style>
      </ModalContent>
    </Modal>
  );
};

export default TourWelcomeModal;
